import { useState, useMemo } from 'react'
import { RefreshCw, AlertCircle, Filter } from 'lucide-react'
import TransactionsTable from '../components/revenue/TransactionsTable'
import { Select } from '../components/ui/Select'
import { useTransactions } from '../hooks/useRevenue'

const PAGE_SIZE = 50

const REVENUE_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'campaign_fee', label: 'Campaign Fees' },
  { value: 'survey_commission', label: 'Survey Commissions' },
]

export default function Transactions() {
  const [page, setPage] = useState(0)
  const [revenueType, setRevenueType] = useState('')

  const params = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      revenue_type: revenueType || undefined,
    }),
    [page, revenueType]
  )

  const { data, loading, error, refetch } = useTransactions(params)

  const handleRevenueTypeChange = (value: string) => {
    setRevenueType(value)
    setPage(0) // Reset to first page when filter changes
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0) {
      setPage(newPage)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Transactions</h1>
          <p className="text-sm sm:text-base text-slate-400 mt-1">
            View and export individual revenue transactions
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={loading}
          className="btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2 text-slate-400">
            <Filter size={16} />
            <span className="text-sm font-medium">Filters</span>
          </div>
          <Select
            value={revenueType}
            onChange={handleRevenueTypeChange}
            options={REVENUE_TYPE_OPTIONS}
            className="w-full sm:w-48"
          />
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
          <AlertCircle size={20} className="text-red-400" />
          <div>
            <p className="font-medium text-red-400">Error loading transactions</p>
            <p className="text-sm text-red-400/80">{error}</p>
          </div>
          <button
            onClick={() => refetch()}
            className="ml-auto text-sm text-red-400 hover:text-red-300 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Transactions Table */}
      <TransactionsTable
        data={data}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
      />
    </div>
  )
}
